import { Component, type ErrorInfo, type ReactNode } from 'react'
import { trackEvent } from '../../lib/analytics'
import { tc } from '../../i18n'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  // Overrides the default fallback headline (e.g. a compact inline message for a
  // wrapped sub-panel). Ignored when `fallback` is supplied.
  title?: string
  // Admin-only diagnostic mode: renders the caught error's name, message,
  // stack and component stack inline instead of letting React 19 unmount the
  // whole tree, logs to the console, and sends no analytics event. Without it
  // an admin dashboard render throw leaves a blank page with no on-page
  // diagnostics. Never use it on a user-facing surface - it prints stacks.
  showErrorDetail?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
  info: string | null
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, info: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, info: null }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.props.showErrorDetail) {
      // Log to console too so the original error survives even if
      // React 19's reportError shim itself fails (the issue this
      // mode exists to defend against).
      // eslint-disable-next-line no-console
      console.error('Dashboard ErrorBoundary caught:', error, info)
      this.setState({ info: info.componentStack ?? null })
      return
    }
    trackEvent('error', { message: error.message?.slice(0, 100) || 'unknown' })
  }

  render() {
    if (this.state.hasError && this.props.showErrorDetail && this.state.error) {
      const { error, info } = this.state
      return (
        <div className="max-w-3xl mx-auto px-4 py-8 space-y-3">
          {/* The admin dashboard's own boundary (showErrorDetail): staff-only,
              English by decision, like the rest of admin. */}
          <h1 className="text-lg font-semibold text-red-400">
            {/* eslint-disable-next-line i18next/no-literal-string -- admin-only (English by decision) */}
            {'Dashboard render failed'}
          </h1>
          <pre className="text-xs text-red-300 bg-black/40 p-3 rounded overflow-auto">
            {error.name}: {error.message}
            {'\n\n'}
            {error.stack ?? ''}
          </pre>
          {info && (
            <details className="text-xs text-gray-400">
              {/* eslint-disable-next-line i18next/no-literal-string -- admin-only (English by decision) */}
              <summary className="cursor-pointer">{'Component stack'}</summary>
              <pre className="mt-2 bg-black/40 p-3 rounded overflow-auto">{info}</pre>
            </details>
          )}
        </div>
      )
    }

    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 space-y-2">
          {/* A class component: tc reads the active language at render. The
              language only changes at bootstrap, so no subscription is needed. */}
          <p className="text-sm text-red-400 font-medium">{this.props.title ?? tc('error_boundary.title')}</p>
          <p className="text-xs text-red-500/70">{this.state.error?.message}</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null, info: null })}
            className="text-xs text-gray-400 hover:text-white underline"
          >
            {tc('error_boundary.try_again')}
          </button>
        </div>
      )
    }

    return this.props.children
  }
}
